'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import {
  ArrowRight,
  BookOpen,
  BrainCircuit,
  Clock3,
  Landmark,
  Building2,
  Sparkles,
  UserRoundCheck,
} from 'lucide-react'
import { PageHero } from '../../components/ui/kit'
import {
  AIRecommendation,
  Badge,
  ButtonLink,
  Card,
  DashboardSection,
  MetricPill,
  PremiumCard,
  StatusBadge,
} from '../../components/ui'
import { estimateReadTime, excerptFrom } from '../../lib/blog-content'

interface ApiPost {
  id: string
  slug: string
  title: string
  excerpt?: string | null
  content?: string | null
  cover_image?: string | null
  tags?: string[] | null
  featured?: boolean | null
  scholar_name?: string | null
  views?: number | null
  published_at?: string | null
  created_at?: string | null
}

interface UiPost {
  slug: string
  title: string
  excerpt: string
  author: string
  category: string
  readTime: number
  date: string
  featured: boolean
}

const categories = ['All', 'Fiqh', 'Ibadah', 'Waqf', 'Society', 'History', 'Quran', 'Hadith']

function iconFor(category: string) {
  if (category === 'Waqf') return Landmark
  if (category === 'Fiqh') return Building2
  if (category === 'Society') return UserRoundCheck
  return BookOpen
}

function fmtDate(s?: string | null) {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function toUi(p: ApiPost): UiPost {
  return {
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt || excerptFrom(p.content, 150) || '',
    author: p.scholar_name || 'Masjids.in Editorial',
    category: (p.tags && p.tags[0]) || 'Article',
    readTime: estimateReadTime(p.content),
    date: fmtDate(p.published_at || p.created_at),
    featured: !!p.featured,
  }
}

export default function BlogPage() {
  const [category, setCategory] = useState('All')
  const [posts, setPosts] = useState<UiPost[]>([])
  const [loading, setLoading] = useState(true)
  // This client page has no server-side flag prop; read the app's existing public flags endpoint.
  // Giving is dark while `campaigns` is OFF (/give 404s) → hide the campaign CTAs until it's ON.
  const [campaignsOn, setCampaignsOn] = useState(false)

  useEffect(() => {
    let active = true
    fetch('/api/feature-flags')
      .then((r) => (r.ok ? r.json() : {}))
      .then((f: any) => { if (active) setCampaignsOn(f?.campaigns === true) })
      .catch(() => {})
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    fetch('/api/blog?status=published&limit=60')
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => {
        if (!active) return
        const list: ApiPost[] = Array.isArray(d?.posts) ? d.posts : []
        setPosts(list.map(toUi))
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  const filtered = useMemo(
    () => posts.filter((post) => category === 'All' || post.category === category),
    [posts, category]
  )
  const featured = posts.find((post) => post.featured) || posts[0]

  return (
    <main className="detail-page">
      <div className="detail-shell">
        <PageHero
          compact
          motif="articles"
          eyebrow="Knowledge and civic intelligence publication"
          title={<>A calmer publication for knowledge, institutions, and civic action.</>}
          subtitle="Editorial articles connected to masjids, waqf protection, campaigns, prayer life, youth programs, and real community needs."
          stats={[
            { value: posts.length, label: 'Articles' },
            { value: categories.length - 1, label: 'Topics' },
          ]}
          widget={
            featured ? (
              <Card padding="lg" className="detail-stack">
                <div className="detail-card-head">
                  <div>
                    <span><Sparkles size={13} /> Featured article</span>
                    <h2>{featured.title}</h2>
                  </div>
                  <Badge tone="gold">Featured</Badge>
                </div>
                <p className="detail-muted">{featured.excerpt}</p>
                <div className="detail-chip-row">
                  <StatusBadge tone="emerald">{featured.author}</StatusBadge>
                  <StatusBadge tone="info">{featured.readTime} min read</StatusBadge>
                </div>
                <ButtonLink href={`/blog/${featured.slug}`} rightIcon={<ArrowRight size={14} />}>Read featured article</ButtonLink>
              </Card>
            ) : undefined
          }
        />

        <div className="detail-grid-wide">
          <section className="detail-stack">
            <div className="detail-tabs">
              {categories.map((item) => (
                <button key={item} type="button" className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)}>
                  {item}
                </button>
              ))}
            </div>

            <div className="detail-editorial-grid">
              <Card padding="lg">
                <div className="detail-card-head">
                  <div>
                    <span><BrainCircuit size={13} /> About this publication</span>
                    <h2>Knowledge you can act on</h2>
                  </div>
                </div>
                <p className="detail-muted">
                  Every article connects to something real on the platform — a practice you can adopt, a waqf issue you can help protect, a campaign you can support, or a masjid you can visit.
                </p>
              </Card>
              <AIRecommendation
                title="Connected ecosystem"
                body="Articles link to real masjids, waqf records, and live campaigns — so what you read connects to what you can do."
                action={campaignsOn ? <ButtonLink href="/give?tab=campaigns" size="sm" variant="secondary">Support a campaign</ButtonLink> : undefined}
              />
            </div>

            <DashboardSection title="Latest Editorials" eyebrow="Knowledge with civic context">
              {loading ? (
                <p className="detail-muted">Loading articles…</p>
              ) : filtered.length === 0 ? (
                <Card padding="lg">
                  <p className="detail-muted" style={{ margin: 0 }}>
                    {posts.length === 0 ? 'No articles have been published yet — check back soon.' : 'No articles in this topic yet.'}
                  </p>
                </Card>
              ) : (
                <div className="entry-card-grid">
                  {filtered.map((post) => {
                    const Icon = iconFor(post.category)
                    return (
                      <Link key={post.slug} href={`/blog/${post.slug}`} className="detail-article-card">
                        <PremiumCard className="detail-article-card">
                          <div className="detail-card-head">
                            <span className="detail-icon-box"><Icon size={18} /></span>
                            <StatusBadge tone={post.featured ? 'gold' : 'info'}>{post.category}</StatusBadge>
                          </div>
                          <h3>{post.title}</h3>
                          <p>{post.excerpt}</p>
                          <div className="detail-chip-row">
                            <StatusBadge tone="emerald">{post.author}</StatusBadge>
                            <span className="detail-muted"><Clock3 size={13} /> {post.readTime} min</span>
                          </div>
                          {post.date && <p className="detail-muted">{post.date}</p>}
                        </PremiumCard>
                      </Link>
                    )
                  })}
                </div>
              )}
            </DashboardSection>
          </section>

          <aside className="detail-rail">
            <Card padding="lg">
              <div className="detail-card-head">
                <div>
                  <span><UserRoundCheck size={13} /> Author trust</span>
                  <h3>Editorial credibility</h3>
                </div>
              </div>
              <div className="detail-list">
                {['Named author on every article', 'Scholar or legal review labeled', 'Linked to real platform actions'].map((item) => (
                  <div key={item} className="detail-list-item">
                    <span className="detail-muted">{item}</span>
                    <StatusBadge tone="emerald">Policy</StatusBadge>
                  </div>
                ))}
              </div>
            </Card>
            <Card padding="lg">
              <div className="detail-card-head">
                <div>
                  <span><Sparkles size={13} /> Connected ecosystem</span>
                  <h3>Related pathways</h3>
                </div>
              </div>
              <div className="detail-actions">
                <ButtonLink href="/masjids" size="sm" variant="secondary">Find masjids</ButtonLink>
                {campaignsOn ? <ButtonLink href="/give?tab=campaigns" size="sm" variant="outline">Support campaigns</ButtonLink> : null}
                <ButtonLink href="/announcements" size="sm" variant="outline">Read notices</ButtonLink>
              </div>
            </Card>
            <DashboardSection title="Topics" eyebrow="Publication map">
              <div className="detail-chip-row">
                {categories.slice(1).map((item) => <StatusBadge key={item} tone="info">{item}</StatusBadge>)}
              </div>
            </DashboardSection>
          </aside>
        </div>
      </div>
    </main>
  )
}
